import { useMemo } from "react";
import { calcRequiredFTE, calcEffectiveHours } from "../../utils/calculations";
import { SlidersHorizontal, Zap } from "lucide-react";

const PRESETS = [
  { label: "Mostly New Hires", util: 55, prod: 65 },
  { label: "Mixed Workforce", util: 75, prod: 75 },
  { label: "Experienced Team", util: 90, prod: 82 },
  { label: "High Performance", util: 100, prod: 110 },
];

function SliderField({ label, value, min, max, step = 1, onChange, colorFn }) {
  const color = colorFn ? colorFn(value) : "#7C9A7E";
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-sage-600 font-medium">{label}</span>
        <span className="text-sm font-heading font-semibold text-sage-900">{value}%</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-1.5 rounded-full cursor-pointer"
        style={{ accentColor: color }}
      />
      <div className="flex justify-between text-[10px] text-sage-400 mt-0.5">
        <span>{min}%</span><span>{max}%</span>
      </div>
    </div>
  );
}

export default function CalibrateTab({ project, updateCalibration, effectiveHours }) {
  const setup = project?.setup || {};
  const stages = project?.stages || [];
  const cal = project?.calibration || { global_utilization: 75, global_productivity: 80, stage_overrides: {} };
  const vc = setup.volume_capacity || {};
  const totalVolume = vc.total_monthly_volume || 0;
  const eff = effectiveHours || calcEffectiveHours(setup.effective_hours);

  const setGlobal = (key, val) => updateCalibration({ ...cal, [key]: val });

  const applyToAll = () => {
    const overrides = {};
    stages.forEach((s, i) => {
      overrides[s.id || i] = { utilization: cal.global_utilization, productivity: cal.global_productivity };
    });
    updateCalibration({ ...cal, stage_overrides: overrides });
  };

  const setStageOverride = (stageId, key, val) => {
    const overrides = { ...cal.stage_overrides, [stageId]: { ...(cal.stage_overrides[stageId] || {}), [key]: val } };
    updateCalibration({ ...cal, stage_overrides: overrides });
  };

  const stagesWithCalc = useMemo(() => {
    return stages.map((s, i) => {
      const stageId = s.id || i;
      const override = cal.stage_overrides?.[stageId] || {};
      const effStage = { ...s, utilization: override.utilization ?? s.utilization ?? 75, productivity: override.productivity ?? s.productivity ?? 80 };
      return { ...s, stageId, override, reqFTE: calcRequiredFTE(effStage, totalVolume, eff) };
    });
  }, [stages, cal.stage_overrides, totalVolume, eff]);

  const totalReqFTE = stagesWithCalc.reduce((a, s) => a + s.reqFTE, 0);

  return (
    <div className="animate-fade-in">
      <div className="mb-4">
        <p className="text-xs font-semibold uppercase tracking-wider text-sage-500 mb-1">Step 3</p>
        <h2 className="font-heading text-2xl font-semibold text-sage-900">Calibrate Parameters</h2>
        <p className="text-sage-500 text-sm mt-1">Adjust global or per-stage utilisation and productivity parameters. All FTE calculations update instantly.</p>
      </div>

      {/* Global Sliders */}
      <div className="bg-white border border-[#E2E8E3] rounded-md p-5 mb-4">
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-sage-50">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-sage-500" />
            <h3 className="font-heading text-base font-semibold text-sage-800">Global Parameters</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => updateCalibration({ ...cal, global_utilization: p.util, global_productivity: p.prod })}
                className="text-xs border border-sage-200 text-sage-600 rounded-md px-2 py-1 hover:bg-sage-50 transition-colors"
                data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <SliderField
            label="Productive Utilisation"
            value={cal.global_utilization ?? 75}
            min={40} max={100}
            onChange={(v) => setGlobal("global_utilization", v)}
            colorFn={(v) => v > 90 ? "#D97706" : "#7C9A7E"}
          />
          <SliderField
            label="Productivity / Efficiency"
            value={cal.global_productivity ?? 80}
            min={40} max={130}
            onChange={(v) => setGlobal("global_productivity", v)}
          />
        </div>
        <button
          data-testid="apply-to-all-button"
          onClick={applyToAll}
          className="flex items-center gap-1.5 bg-sage-600 text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-sage-700 transition-colors"
        >
          <Zap className="w-3 h-3" /> Apply to All Stages
        </button>
      </div>

      {/* Per-stage sliders */}
      <div className="bg-white border border-[#E2E8E3] rounded-md overflow-hidden">
        <div className="px-5 py-3 border-b border-sage-100 bg-sage-50 flex items-center justify-between">
          <h3 className="font-heading text-sm font-semibold text-sage-800">Per-Stage Parameters</h3>
          <div className="text-xs text-sage-500">Total Required FTE: <span className="font-heading font-bold text-sage-900 text-base ml-1">{totalReqFTE.toFixed(1)}</span></div>
        </div>
        {stages.length === 0 ? (
          <div className="p-8 text-center text-sage-400 text-sm">No stages configured. Go to Process Stages tab first.</div>
        ) : (
          <div className="divide-y divide-sage-50">
            {stagesWithCalc.map((stage, idx) => {
              const util = stage.override?.utilization ?? stage.utilization ?? 75;
              const prod = stage.override?.productivity ?? stage.productivity ?? 80;
              return (
                <div key={stage.stageId} className="p-4 hover:bg-sage-50/30 transition-colors" data-testid={`calibrate-stage-${idx}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading text-sm font-semibold text-sage-800">{stage.stage_name}</span>
                        <span className="text-xs bg-sage-100 text-sage-600 px-2 py-0.5 rounded-full">{stage.stage_type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${stage.priority === "Critical" ? "bg-red-100 text-red-600" : stage.priority === "High" ? "bg-orange-100 text-orange-600" : "bg-sage-100 text-sage-600"}`}>{stage.priority}</span>
                      </div>
                      <div className="text-xs text-sage-400 mt-0.5">Local: {stage.local_count || 0} · Expat: {stage.expat_count || 0} · Yield: {stage.yield_pct}%</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-xl font-bold text-sage-900">{stage.reqFTE.toFixed(1)}</div>
                      <div className="text-xs text-sage-400">Req FTE</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <SliderField
                      label="Utilisation %"
                      value={util}
                      min={40} max={100}
                      onChange={(v) => setStageOverride(stage.stageId, "utilization", v)}
                      colorFn={(v) => v > 90 ? "#D97706" : "#7C9A7E"}
                    />
                    <SliderField
                      label="Productivity %"
                      value={prod}
                      min={40} max={130}
                      onChange={(v) => setStageOverride(stage.stageId, "productivity", v)}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
